import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Iconos
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import TerminalIcon from '@mui/icons-material/TerminalRounded';
import StorefrontIcon from '@mui/icons-material/StorefrontRounded';
import WebhookIcon from '@mui/icons-material/WebhookRounded';
import SecurityIcon from '@mui/icons-material/SecurityRounded';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Contexto (Ajusta la ruta según tu proyecto)
import { useOperational } from 'providers/OperationalContextProvider'; 

export default function Documentation() {
  const [activeTab, setActiveTab] = React.useState('ecommerce');

  // Estados para E-commerce SDK
  const { getSdkReleases, sdks } = useOperational();
  const [channelFilter, setChannelFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [copiedId, setCopiedId] = React.useState(null);
  const [copiedCode, setCopiedCode] = React.useState(false);

  React.useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  // Cargar versiones solo si estamos en la pestaña E-commerce
  React.useEffect(() => {
    if (activeTab === 'ecommerce') {
      const fetchReleases = async () => {
        setLoading(true);
        try {
          await getSdkReleases(channelFilter);
        } catch (err) {
          console.error("Error cargando releases:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchReleases();
    }
  }, [channelFilter, activeTab]);

  const handleCopyText = (text, id, isCode = false) => {
    navigator.clipboard.writeText(text);
    if (isCode) {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } else {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getChipColor = (channel) => {
    switch (channel) {
      case 'alpha': return { bg: '#ff9800', text: '#fff' };
      case 'beta': return { bg: '#2196f3', text: '#fff' };
      case 'stable': return { bg: '#4caf50', text: '#fff' };
      default: return { bg: 'rgba(255,255,255,0.2)', text: '#fff' };
    }
  };

  const sampleCode = `import { Leidenfrost } from "@leidenfrost/sdk";

// Inicialización con credenciales de Service Account
export const lf = Leidenfrost.initializeApp({
  type: "service_account",
  clientId: "sa_master_messiah",
  clientEmail: "master-cms@leidenfrost.io",
  privateKeyId: "key_master_e11f32e2",
  privateKey: "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----",
  scopes: ["*"]
});`;

  // MENÚ LATERAL (SIDEBAR)
  const renderSidebar = () => (
    <Box
      sx={{
        width: { xs: '100%', md: 260 },
        flexShrink: 0,
        borderRight: { md: '1px solid rgba(255, 255, 255, 0.1)' },
        pr: { md: 2 },
        mb: { xs: 4, md: 0 },
      }}
    >
      <Box sx={{ position: 'sticky', top: 100 }}>
        <Typography variant="overline" sx={{ color: 'grey.400', fontWeight: 'bold', pl: 2 }}>
          LIBRERÍAS Y SDK
        </Typography>
        <List sx={{ mt: 1 }}>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'ecommerce'}
              onClick={() => setActiveTab('ecommerce')}
              sx={{
                borderRadius: 2,
                mb: 1,
                bgcolor: activeTab === 'ecommerce' ? 'rgba(255,255,255,0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.2)', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: activeTab === 'ecommerce' ? 'white' : 'grey.400' }}>
                <StorefrontIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="E-Commerce SDK" 
                primaryTypographyProps={{ fontWeight: activeTab === 'ecommerce' ? 'bold' : 'medium', fontSize: '0.9rem' }} 
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Typography variant="overline" sx={{ color: 'grey.400', fontWeight: 'bold', pl: 2, mt: 3, display: 'block' }}>
          GUÍAS DE INTEGRACIÓN
        </Typography>
        <List sx={{ mt: 1 }}>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'auth'}
              onClick={() => setActiveTab('auth')}
              sx={{
                borderRadius: 2,
                mb: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'grey.400' }}>
                <SecurityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Autenticación y Sesiones" primaryTypographyProps={{ fontSize: '0.9rem', color: 'grey.300' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'webhooks'}
              onClick={() => setActiveTab('webhooks')}
              sx={{
                borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'grey.400' }}>
                <WebhookIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Webhooks" primaryTypographyProps={{ fontSize: '0.9rem', color: 'grey.300' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  // CONTENIDO E-COMMERCE
  const renderEcommerceContent = () => (
    <Box sx={{ display: 'flex', gap: { xs: 0, lg: 6 }, position: 'relative' }}>
      
      {/* COLUMNA PRINCIPAL DE DOCUMENTACIÓN */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        
        {/* Header Guía */}
        <Box mb={6} data-aos="fade-in">
          <Typography component="h1" variant="h3" fontWeight="bold" gutterBottom>
            Agregar Leidenfrost E-commerce SDK a tu Web App
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.200', fontSize: '1.1rem', maxWidth: 800 }}>
            El SDK e-commerce proporciona librerías cliente en JavaScript/TypeScript 
            para comunicarte con la plataforma multi-tenant, autenticar sesiones y aprovisionar aplicaciones.
          </Typography>
        </Box>

        {/* PASO 1: INSTALACIÓN */}
        <Box id="step-1-install" sx={{ mb: 8, pt: 2 }} data-aos="fade-up">
          <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box component="span" sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}>
              1
            </Box>
            Instalar el paquete vía NPM
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.200', mb: 2 }}>
            Agrega la última versión del paquete como dependencia en tu proyecto React, Next.js o Node.js:
          </Typography>

          <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TerminalIcon sx={{ color: 'grey.400' }} />
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'grey.100', fontWeight: 'bold' }}>
                npm install @leidenfrost/sdk
              </Typography>
            </Stack>
            <Button size="small" onClick={() => handleCopyText('npm install @leidenfrost/sdk', 'npm-main')} sx={{ color: 'white', minWidth: 'auto' }}>
              {copiedId === 'npm-main' ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
            </Button>
          </Paper>
        </Box>

        {/* PASO 2: RELEASES */}
        <Box id="step-2-releases" sx={{ mb: 8, pt: 2 }} data-aos="fade-up">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} mb={3}>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box component="span" sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}>
                2
              </Box>
              Versiones y Descargas Disponibles
            </Typography>

            <ToggleButtonGroup
              value={channelFilter}
              exclusive
              onChange={(e, newChannel) => newChannel && setChannelFilter(newChannel)}
              size="small"
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                '& .MuiToggleButton-root': {
                  color: 'grey.300', borderColor: 'rgba(255, 255, 255, 0.2)', px: 2,
                  '&.Mui-selected': { bgcolor: 'rgba(255, 255, 255, 0.25)', color: 'white', fontWeight: 'bold' },
                },
              }}
            >
              <ToggleButton value="all">Todos</ToggleButton>
              <ToggleButton value="stable">Stable</ToggleButton>
              <ToggleButton value="beta">Beta</ToggleButton>
              <ToggleButton value="alpha">Alpha</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress color="inherit" /></Box>}

          {!loading && (!sdks || sdks.length === 0) && (
            <Paper sx={{ p: 4, bgcolor: 'rgba(0, 0, 0, 0.2)', border: '1px dashed rgba(255,255,255,0.3)', textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'grey.300' }}>No se encontraron versiones para este canal.</Typography>
            </Paper>
          )}

          {!loading && sdks && sdks.length > 0 && (
            <Stack spacing={2.5}>
              {sdks.map((rel, index) => {
                const badgeStyle = getChipColor(rel.channel);
                return (
                  <Paper key={rel.release_id || index} elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: 'rgba(0, 0, 0, 0.25)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, transition: 'all 0.2s', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.4)', bgcolor: 'rgba(0, 0, 0, 0.35)' } }}>
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Chip label={rel.channel?.toUpperCase() || 'RELEASE'} size="small" sx={{ bgcolor: badgeStyle.bg, color: badgeStyle.text, fontWeight: 'bold', fontSize: '0.75rem' }} />
                          <Typography variant="h6" fontWeight="bold">{rel.title}</Typography>
                          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', color: 'grey.300' }}>v{rel.version}</Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="caption" sx={{ color: 'grey.400' }}>
                            Node {rel.min_node_version || '>=18.0.0'} • {rel.published_at ? new Date(rel.published_at).toLocaleDateString() : ''}
                          </Typography>
                          <Button variant="contained" size="small" startIcon={<DownloadIcon />} href={rel.download_url} download sx={{ bgcolor: 'white', color: '#8b261d', fontWeight: 'bold', textTransform: 'none', '&:hover': { bgcolor: 'grey.200' } }}>
                            .tgz
                          </Button>
                        </Stack>
                      </Stack>

                      <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'grey.200' }}>npm i @leidenfrost/sdk@{rel.version}</Typography>
                        <Button size="small" onClick={() => handleCopyText(`npm i @leidenfrost/sdk@${rel.version}`, rel.release_id)} sx={{ color: 'white', minWidth: 'auto', p: 0.5 }}>
                          {copiedId === rel.release_id ? <CheckIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                        </Button>
                      </Box>

                      {rel.changelog && rel.changelog.length > 0 && (
                        <Box sx={{ pt: 1 }}>
                          <Typography variant="caption" fontWeight="bold" sx={{ color: 'grey.300', mb: 0.5, display: 'block' }}>Novedades de esta versión:</Typography>
                          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                            {rel.changelog.map((item, idx) => (
                              <Typography component="li" key={idx} variant="caption" sx={{ color: 'grey.200', mb: 0.3 }}>{item}</Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* PASO 3: INICIALIZACIÓN */}
        <Box id="step-3-init" sx={{ mb: 6, pt: 2 }} data-aos="fade-up">
          <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box component="span" sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}>
              3
            </Box>
            Inicializar el SDK en tu aplicación
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.200', mb: 2 }}>
            Instancia la clase principal configurando la Service Account Master o la credencial generada desde tu panel:
          </Typography>

          <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, position: 'relative' }}>
            <Button size="small" onClick={() => handleCopyText(sampleCode, 'code', true)} sx={{ position: 'absolute', top: 12, right: 12, color: 'white' }}>
              {copiedCode ? <CheckIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </Button>
            <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'grey.100', m: 0, overflowX: 'auto' }}>
              {sampleCode}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* TABLE OF CONTENTS (DERECHA) - SCROLLSPY ESTATICO */}
      <Box sx={{ width: 220, display: { xs: 'none', lg: 'block' }, flexShrink: 0 }}>
        <Box sx={{ position: 'sticky', top: 100, p: 2, borderLeft: '2px solid rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'white' }}>En esta página</Typography>
          <Stack spacing={1.5}>
            <Typography variant="caption" onClick={() => scrollToSection('step-1-install')} sx={{ color: 'grey.400', cursor: 'pointer', '&:hover': { color: 'white' } }}>1. Instalación NPM</Typography>
            <Typography variant="caption" onClick={() => scrollToSection('step-2-releases')} sx={{ color: 'grey.400', cursor: 'pointer', '&:hover': { color: 'white' } }}>2. Versiones y Releases</Typography>
            <Typography variant="caption" onClick={() => scrollToSection('step-3-init')} sx={{ color: 'grey.400', cursor: 'pointer', '&:hover': { color: 'white' } }}>3. Inicialización</Typography>
          </Stack>
        </Box>
      </Box>

    </Box>
  );

  return (
    <Box
      sx={{
        pt: { xs: 4, sm: 10 },
        pb: { xs: 8, sm: 16 },
        minHeight: '100vh',
        color: 'white',
        bgcolor: '#125eac', // Rojo tipo ladrillo
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {renderSidebar()}
          
          <Box sx={{ flexGrow: 1, pl: { md: 4 } }}>
            {activeTab === 'ecommerce' && renderEcommerceContent()}
            {activeTab === 'auth' && (
              <Typography variant="h4" fontWeight="bold">Documentación de Autenticación (Próximamente)</Typography>
            )}
            {activeTab === 'webhooks' && (
              <Typography variant="h4" fontWeight="bold">Documentación de Webhooks (Próximamente)</Typography>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}